import React, { useCallback, useMemo } from 'react';
import { Box, Chip, ListItem, ListItemIcon, ListItemText } from '@mui/material';
// (useMemo usado para externalLinks)
import { useHistory } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ExternalMenu, ExternalPublicMenu, Menus, MenuTypes } from 'config/menus';
import { falsy, getItemInStorage } from 'utils/functions';
import { checkViewPermissions } from 'utils/helpers/helper';
import { listItemStyles } from '../styles';
import { navItemSx, NAV_ITEM_FONT_SIZE } from './styles';

/**
 * Lógica compartida de las variantes del sidebar: construye un `mapView` que
 * filtra por permisos/área y expone helpers para armar ítems sueltos y
 * categorías a partir de las keys de `Menus`.
 */
export function useSideBarItems({ user, actions, globals }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const history = useHistory();

  const onClickMenu = useCallback(
    (path, e) => {
      e.preventDefault();
      e.stopPropagation();
      actions.toggleDrawer();
      actions.globalFiltersReset();
      history.push(path);
    },
    [actions, history],
  );

  // Devuelve el <ListItem> de un ítem de menú, o null si no hay permisos/área.
  const mapView = useCallback(
    (index, path, options) => {
      if (!options) return null;
      const type = options.type ?? MenuTypes.PUBLIC;
      const anonymousCheck = type === MenuTypes.ANONYMOUS && falsy(user);
      const canView = anonymousCheck
        ? true
        : checkViewPermissions(user?.roleCode, options.permissionPath, user, options.areaID);

      if ((!anonymousCheck && !canView) || (isMobile && options.displayMobile === 'restrict')) {
        return null;
      }

      const Icon = options.icon;
      const isNew = typeof options.extraText === 'string' && /NUEVO/i.test(options.extraText);

      return (
        <ListItem
          style={listItemStyles}
          onClick={(e) => onClickMenu(path, e)}
          button
          key={`${index}-${path}`}
          component="a"
          href={path}
          sx={navItemSx}>
          <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
            <Icon />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ style: { fontSize: NAV_ITEM_FONT_SIZE } }}
            primary={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>{options.name}</span>
                {options.extraText && (
                  <Chip
                    label={options.extraText.trim()}
                    size="small"
                    color={isNew ? 'primary' : 'default'}
                    sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem' } }}
                  />
                )}
              </Box>
            }
          />
        </ListItem>
      );
    },
    [onClickMenu, user, isMobile],
  );

  // Mapea una lista de paths a vistas, descartando los que no pasan permisos.
  // Los ítems marcados como novedad (extraText "NUEVO") se ordenan primero.
  // Con `sortAlpha`, el resto se ordena alfabéticamente por nombre.
  const mapPaths = useCallback(
    (paths, keyPrefix, { sortAlpha = false } = {}) => {
      const isNew = (path) => {
        const o = Menus[path];
        return typeof o?.extraText === 'string' && /NUEVO/i.test(o.extraText);
      };
      const ordered = [...paths].sort((a, b) => {
        const newDiff = Number(isNew(b)) - Number(isNew(a));
        if (newDiff !== 0) return newDiff; // novedades primero
        if (!sortAlpha) return 0;
        const nameA = Menus[a]?.name ?? '';
        const nameB = Menus[b]?.name ?? '';
        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
      });
      return ordered
        .map((path, i) => mapView(`${keyPrefix}-${i}`, path, Menus[path]))
        .filter((v) => v != null);
    },
    [mapView],
  );

  // Enlaces externos (Login Externo Bienes/Propiedades + públicos).
  const externalLinks = useMemo(() => {
    const session = getItemInStorage('user');
    const hasLink = globals?.rolExternalLink;
    const options = [];
    if (hasLink && user?.id) {
      Object.values(ExternalMenu(session, hasLink)).forEach((link) => {
        if (checkViewPermissions(user?.roleCode, 'external', {})) options.push(link);
      });
    }
    Object.values(ExternalPublicMenu()).forEach((link) => link && options.push(link));

    return options.map(({ Icon, path, name }, ind) => (
      <ListItem
        button
        style={listItemStyles}
        component="a"
        href={path}
        target="_blank"
        key={`external-${ind}`}
        sx={navItemSx}>
        <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
          <Icon />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ style: { fontSize: NAV_ITEM_FONT_SIZE } }}
          primary={name}
        />
      </ListItem>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, globals?.rolExternalLink]);

  return { mapView, mapPaths, externalLinks, isMobile };
}
